import { DEFAULT_RADAR_THEME, RADAR_THEMES, type RadarTheme } from '@disa/map-data';
import type { Palette, TrajectoryVisibility } from '@disa/plate';

/** Where the scoreboard stands — `docs/DESIGN.md` §5.2. */
export type ScoreboardPosition = 'block' | 'plate';

/* The colour set (`docs/DESIGN.md` §2.4's data colours, the colour-blind-safe variant or the
   cyberpunk look's — #339) and the grenade paths drawn are what a plate is told, so their types
   live in `@disa/plate`. */
export type { Palette, TrajectoryVisibility };

/** What `prefers-reduced-motion` says, or the reader's own answer over the top of it. */
export type MotionPreference = 'system' | 'reduced' | 'full';

/** How far `←` and `→` move the clock — §9.1. */
export type SeekStepSeconds = 5 | 10 | 15;

/** How much faster a held `←` or `→` moves than a tapped one — §9.1. */
export type HeldArrowRate = 2 | 4;

/**
 * Every preference `docs/DESIGN.md` §10.5 names, except the locale: that one is read before React
 * starts and owned by `@disa/i18n`, which is the package that reads its storage key at boot.
 *
 * The four `Axis` flags are the exception in the other direction — §10.5's table does not name them
 * and the settings sheet does not draw them. They are the round axis's filter, and a filter is
 * flipped while looking at what it changes: a sheet that covers the screen is the wrong place for a
 * control whose whole feedback is on the screen behind it. What they share with the table is where
 * they are *kept*, which is this file, because a preference that outlives a session is a preference
 * however it is reached.
 */
export interface Settings {
  readonly isBuyPhaseSkipped: boolean;
  readonly seekStepSeconds: SeekStepSeconds;
  readonly heldArrowRate: HeldArrowRate;
  readonly radarTheme: RadarTheme;
  readonly isAudibilityShown: boolean;
  readonly arePlayerNamesShown: boolean;
  readonly trajectories: TrajectoryVisibility;
  readonly scoreboard: ScoreboardPosition;
  readonly areSurvivorsShown: boolean;
  readonly areAxisKillsShown: boolean;
  readonly isAxisUtilityShown: boolean;
  readonly areAxisObjectivesShown: boolean;
  readonly isAxisSelectedOnly: boolean;
  readonly palette: Palette;
  readonly motion: MotionPreference;
  readonly isDebugShown: boolean;
  readonly isPlayerKeysShown: boolean;
  readonly isPlayerCrosshairShown: boolean;
  /** The Home grid the reader arranged, as the JSON `features/library` writes. Empty is the default. */
  readonly homeLayout: string;
  /** The SteamID64 the Home stats widget looks for in the last match. Empty is unset. */
  readonly homeSteamId: string;
  /** Where the last opened match was left, as the JSON `features/library` writes. Empty is none. */
  readonly homeReading: string;
}

export type SettingKey = keyof Settings;

interface Descriptor<T> {
  readonly storageKey: string;
  readonly fallback: T;
  /** `undefined` for anything this setting does not recognise, which then falls back. */
  parse(raw: string): T | undefined;
  format(value: T): string;
}

function flag(storageKey: string, fallback: boolean): Descriptor<boolean> {
  return {
    storageKey,
    fallback,
    parse: (raw) => (raw === 'true' ? true : raw === 'false' ? false : undefined),
    format: String,
  };
}

function text(storageKey: string): Descriptor<string> {
  return { storageKey, fallback: '', parse: (raw) => raw, format: (value) => value };
}

function choice<T extends string>(
  storageKey: string,
  options: readonly T[],
  fallback: T,
): Descriptor<T> {
  return {
    storageKey,
    fallback,
    parse: (raw) => options.find((option) => option === raw),
    format: (value) => value,
  };
}

function numberChoice<T extends number>(
  storageKey: string,
  options: readonly T[],
  fallback: T,
): Descriptor<T> {
  return {
    storageKey,
    fallback,
    parse: (raw) => options.find((option) => String(option) === raw),
    format: String,
  };
}

export const SEEK_STEPS: readonly SeekStepSeconds[] = [5, 10, 15];
export const HELD_ARROW_RATES: readonly HeldArrowRate[] = [2, 4];
export const TRAJECTORY_VISIBILITIES: readonly TrajectoryVisibility[] = [
  'flight',
  'selected',
  'off',
];
export const SCOREBOARD_POSITIONS: readonly ScoreboardPosition[] = ['block', 'plate'];
export const PALETTES: readonly Palette[] = ['default', 'colour-blind', 'cyber'];
export const MOTION_PREFERENCES: readonly MotionPreference[] = ['system', 'reduced', 'full'];

/**
 * One storage key per setting rather than one document: a preference is readable and clearable on
 * its own, and four of these keys were written by earlier issues and keep the names they had.
 */
const DESCRIPTORS: { readonly [K in SettingKey]: Descriptor<Settings[K]> } = {
  isBuyPhaseSkipped: flag('disa.playback.skipBuyPhase', false),
  seekStepSeconds: numberChoice('disa.playback.seekStep', SEEK_STEPS, 10),
  heldArrowRate: numberChoice('disa.playback.heldArrowRate', HELD_ARROW_RATES, 2),
  radarTheme: choice('disa.radar.theme', RADAR_THEMES, DEFAULT_RADAR_THEME),
  isAudibilityShown: flag('disa.radar.audibility', false),
  arePlayerNamesShown: flag('disa.radar.playerNames', true),
  trajectories: choice('disa.radar.trajectories', TRAJECTORY_VISIBILITIES, 'flight'),
  scoreboard: choice('disa.review.scoreboard', SCOREBOARD_POSITIONS, 'block'),
  areSurvivorsShown: flag('disa.timeline.survivors', false),
  areAxisKillsShown: flag('disa.timeline.axis.kills', true),
  isAxisUtilityShown: flag('disa.timeline.axis.utility', true),
  areAxisObjectivesShown: flag('disa.timeline.axis.objectives', true),
  isAxisSelectedOnly: flag('disa.timeline.axis.selectedOnly', false),
  palette: choice('disa.palette', PALETTES, 'default'),
  motion: choice('disa.motion', MOTION_PREFERENCES, 'system'),
  isDebugShown: flag('disa.radar.debug', false),
  isPlayerKeysShown: flag('disa.spectator.keys', true),
  isPlayerCrosshairShown: flag('disa.spectator.crosshair', false),
  homeLayout: text('disa.home.layout'),
  homeSteamId: text('disa.home.steamId'),
  homeReading: text('disa.home.reading'),
};

export const SETTING_KEYS = Object.keys(DESCRIPTORS) as readonly SettingKey[];

export function storageKeyOf(key: SettingKey): string {
  return DESCRIPTORS[key].storageKey;
}

export const DEFAULT_SETTINGS: Settings = {
  isBuyPhaseSkipped: DESCRIPTORS.isBuyPhaseSkipped.fallback,
  seekStepSeconds: DESCRIPTORS.seekStepSeconds.fallback,
  heldArrowRate: DESCRIPTORS.heldArrowRate.fallback,
  radarTheme: DESCRIPTORS.radarTheme.fallback,
  isAudibilityShown: DESCRIPTORS.isAudibilityShown.fallback,
  arePlayerNamesShown: DESCRIPTORS.arePlayerNamesShown.fallback,
  trajectories: DESCRIPTORS.trajectories.fallback,
  scoreboard: DESCRIPTORS.scoreboard.fallback,
  areSurvivorsShown: DESCRIPTORS.areSurvivorsShown.fallback,
  areAxisKillsShown: DESCRIPTORS.areAxisKillsShown.fallback,
  isAxisUtilityShown: DESCRIPTORS.isAxisUtilityShown.fallback,
  areAxisObjectivesShown: DESCRIPTORS.areAxisObjectivesShown.fallback,
  isAxisSelectedOnly: DESCRIPTORS.isAxisSelectedOnly.fallback,
  palette: DESCRIPTORS.palette.fallback,
  motion: DESCRIPTORS.motion.fallback,
  isDebugShown: DESCRIPTORS.isDebugShown.fallback,
  isPlayerKeysShown: DESCRIPTORS.isPlayerKeysShown.fallback,
  isPlayerCrosshairShown: DESCRIPTORS.isPlayerCrosshairShown.fallback,
  homeLayout: DESCRIPTORS.homeLayout.fallback,
  homeSteamId: DESCRIPTORS.homeSteamId.fallback,
  homeReading: DESCRIPTORS.homeReading.fallback,
};

export type StoredValues = (storageKey: string) => string | null;

function readOne<K extends SettingKey>(key: K, read: StoredValues): Settings[K] {
  const descriptor = DESCRIPTORS[key];
  const raw = read(descriptor.storageKey);

  if (raw === null) return descriptor.fallback;

  return descriptor.parse(raw) ?? descriptor.fallback;
}

/**
 * The settings a store of raw strings describes. A value the descriptor does not recognise falls
 * back rather than failing: a preference written by an older build is not a reason to refuse to
 * open a match.
 */
export function settingsFrom(read: StoredValues): Settings {
  return {
    isBuyPhaseSkipped: readOne('isBuyPhaseSkipped', read),
    seekStepSeconds: readOne('seekStepSeconds', read),
    heldArrowRate: readOne('heldArrowRate', read),
    radarTheme: readOne('radarTheme', read),
    isAudibilityShown: readOne('isAudibilityShown', read),
    arePlayerNamesShown: readOne('arePlayerNamesShown', read),
    trajectories: readOne('trajectories', read),
    scoreboard: readOne('scoreboard', read),
    areSurvivorsShown: readOne('areSurvivorsShown', read),
    areAxisKillsShown: readOne('areAxisKillsShown', read),
    isAxisUtilityShown: readOne('isAxisUtilityShown', read),
    areAxisObjectivesShown: readOne('areAxisObjectivesShown', read),
    isAxisSelectedOnly: readOne('isAxisSelectedOnly', read),
    palette: readOne('palette', read),
    motion: readOne('motion', read),
    isDebugShown: readOne('isDebugShown', read),
    isPlayerKeysShown: readOne('isPlayerKeysShown', read),
    isPlayerCrosshairShown: readOne('isPlayerCrosshairShown', read),
    homeLayout: readOne('homeLayout', read),
    homeSteamId: readOne('homeSteamId', read),
    homeReading: readOne('homeReading', read),
  };
}

export function formatSetting<K extends SettingKey>(key: K, value: Settings[K]): string {
  return DESCRIPTORS[key].format(value);
}
